/* eslint-disable unicorn/no-null -- the engine's reward contract uses `null` to mean "no reward / skip update". */
import { NudgingConstants } from '@convegenius/nudging-engine';

import Constants from '@/Constants';

export interface NudgingEvent {
  name: string;
  params?: Record<string, unknown>;
  timestamp?: number;
}

export type RewardStrategy = (event: NudgingEvent) => number | null;

const REWARD_FULL = 1;
const REWARD_PARTIAL = 0.5;
const REWARD_NONE = 0;

const { NUDGE_EVENT_NAMES, NUDGE_EVENT_PARAMETERS } = Constants;

const boolParameter = (event: NudgingEvent, key: string): boolean => event.params?.[key] === true;

const scoreQuestionAnswered = (event: NudgingEvent): number => {
  const isCorrect = boolParameter(event, NUDGE_EVENT_PARAMETERS.LABEL);
  const hintUsed = boolParameter(event, NUDGE_EVENT_PARAMETERS.VALUE);
  if (isCorrect && !hintUsed) return REWARD_FULL;
  if (isCorrect && hintUsed) return REWARD_PARTIAL;
  return REWARD_NONE;
};

export const landingPageRewardStrategy: RewardStrategy = (event) => {
  switch (event.name) {
    case NUDGE_EVENT_NAMES.STAGE_STARTED:
    case NUDGE_EVENT_NAMES.MISSION_PRACTICED: {
      return REWARD_FULL;
    }
    case NUDGE_EVENT_NAMES.STUDENT_LOG_OUT: {
      return REWARD_NONE;
    }
    default: {
      return null;
    }
  }
};

export const learningPageRewardStrategy: RewardStrategy = (event) => {
  switch (event.name) {
    case NUDGE_EVENT_NAMES.CUEING_STARTED:
    case NUDGE_EVENT_NAMES.VIDEO_COMPLETED: {
      return REWARD_FULL;
    }
    case NUDGE_EVENT_NAMES.STAGE_EXITED: {
      return REWARD_NONE;
    }
    default: {
      return null;
    }
  }
};

export const formativePageRewardStrategy: RewardStrategy = (event) => {
  switch (event.name) {
    case NUDGE_EVENT_NAMES.HINT_CLICKED: {
      return REWARD_PARTIAL;
    }
    case NUDGE_EVENT_NAMES.STAGE_EXITED: {
      return REWARD_NONE;
    }
    case NUDGE_EVENT_NAMES.QUESTION_ANSWERED: {
      return scoreQuestionAnswered(event);
    }
    default: {
      return null;
    }
  }
};

export const cueingPageRewardStrategy: RewardStrategy = (event) => {
  switch (event.name) {
    case NUDGE_EVENT_NAMES.QUESTION_ANSWERED: {
      return boolParameter(event, NUDGE_EVENT_PARAMETERS.LABEL) ? REWARD_FULL : REWARD_NONE;
    }
    case NUDGE_EVENT_NAMES.CUEING_COMPLETED: {
      return REWARD_FULL;
    }
    case NUDGE_EVENT_NAMES.CUEING_FAILED:
    case NUDGE_EVENT_NAMES.STAGE_EXITED: {
      return REWARD_NONE;
    }
    default: {
      return null;
    }
  }
};

export const practicePageRewardStrategy: RewardStrategy = (event) => {
  switch (event.name) {
    case NUDGE_EVENT_NAMES.QUESTION_ANSWERED: {
      return scoreQuestionAnswered(event);
    }
    case NUDGE_EVENT_NAMES.HINT_CLICKED: {
      return REWARD_PARTIAL;
    }
    case NUDGE_EVENT_NAMES.STAGE_EXITED: {
      return REWARD_NONE;
    }
    default: {
      return null;
    }
  }
};

function noOpRewardStrategy(): number | null {
  return null;
}

export function rewardStrategyForMode(mode: string): RewardStrategy {
  switch (mode) {
    case NudgingConstants.MODE_LANDING: {
      return landingPageRewardStrategy;
    }
    case NudgingConstants.MODE_FORMATIVE: {
      return formativePageRewardStrategy;
    }
    case NudgingConstants.MODE_EVALUATIVE: {
      return cueingPageRewardStrategy;
    }
    case NudgingConstants.MODE_VIDEO: {
      return learningPageRewardStrategy;
    }
    case NudgingConstants.MODE_PRACTICE: {
      return practicePageRewardStrategy;
    }
    default: {
      return noOpRewardStrategy;
    }
  }
}
